import React, { useEffect, useState } from "react";

import {
  Button, Flex, Form, Input, Modal,
  Switch,
} from "antd";
import { toast } from "react-toastify";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { createMlQcRule, updateMlQcRule } from "../../../../actions/qc";
import { RuleStatusMap } from "../utils";
import { getUser } from "../../../../utils/helpers";

import "./EditQcRuleModal.css";

// Initial form values with at least one rule detail
const INITIAL_FORM_VALUES = {
  rule_type: "",
  rule_description: "",
  rule_details: [
    {
      condition: "",
    },
  ],
  is_active: true,
};

export default function EditQcRuleModal({
  open,
  onCancel,
  editData,
  refetch,
}) {
  const isEdit = !!editData;
  const [form] = Form.useForm();
  const status = Form.useWatch("is_active", form);
  const [loading, setLoading] = useState(false);

  /**
   * updated form fields if edit mode
   */
  useEffect(() => {
    if (editData) {
      form.setFieldsValue({
        rule_type: editData.rule_type ?? INITIAL_FORM_VALUES.rule_type,
        rule_description: editData.rule_description ?? INITIAL_FORM_VALUES.rule_description,
        rule_details: editData.rule_details?.conditions?.length
          ? editData.rule_details?.conditions?.map((condition) => ({ condition: condition.trim() }))
          : INITIAL_FORM_VALUES.rule_details,
        is_active: editData.is_active,
      });
    }
  }, [editData, form]);

  /**
   * handles submitting form
   * @param {*} values form values
   */
  const onFinish = async (values) => {
    const payload = {
      user_id: getUser().id,
      ...values,
      rule_details: {
        conditions: values.rule_details.map((item) => item.condition.trim()),
      },
    };

    let response;
    setLoading(true);
    if (!isEdit) {
      // create new rule
      response = await createMlQcRule(payload);
    } else {
      // edit a rule
      payload.rule_id = editData.rule_id;
      response = await updateMlQcRule(payload);
    }
    setLoading(false);

    const successMessage = response.message || (isEdit ? "Rule updated" : "Rule created");
    const errorMessage = response.message || "Something went wrong!";
    if (response.success) {
      toast.success(successMessage);
      onCancel();
      refetch();
    } else {
      toast.error(errorMessage);
    }
  };

  /**
   * handles clicking cancel button
   */
  const onCancelForm = () => {
    form.resetFields();
    onCancel();
  };

  /**
   * Form validation for required field with custom message
   * @param {string} message error message
   */
  const requiredFormValidation = (message) => ({
    required: true,
    message,
    transform: (value) => value?.trim(),
  });

  return (
    <Modal
      centered
      title={!isEdit ? "Add New Rule" : `Edit Rule ${editData.rule_id}`}
      width={900}
      open={open}
      onCancel={onCancel}
      rootClassName="EditQcRuleModal"
      footer={(
        <Flex className="EditQcRuleModal__Footer" align="center" justify="flex-end" gap={8}>
          <Button
            type="default"
            onClick={onCancelForm}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            onClick={form.submit}
            loading={loading}
          >
            Save
          </Button>
        </Flex>
      )}
    >
      <Form
        form={form}
        name="qc_rules"
        initialValues={INITIAL_FORM_VALUES}
        onFinish={onFinish}
        autoComplete="off"
        colon={false}
        layout="vertical"
        rootClassName="EditQcRuleModal__Form"
        requiredMark={false}
      >
        <Flex align="center" gap={16}>
          <Form.Item
            label="Type"
            name="rule_type"
            rules={[requiredFormValidation("Type required")]}
            className="EditQcRuleModal__RuleInput"
          >
            <Input
              placeholder="Type"
              disabled={isEdit}
              className="EditQcRuleModal__Input"
            />
          </Form.Item>

          <Flex align="center" justify="flex-start" gap={4} className="EditQcRuleModal__StatusContainer">
            <Form.Item
              label="Status"
              name="is_active"
              valuePropName="checked"
            >
              <Switch
                defaultChecked
              />
            </Form.Item>
            <span>{status ? RuleStatusMap.Active.label : RuleStatusMap.Inactive.label}</span>
          </Flex>
        </Flex>

        <Form.Item
          label="Description"
          name="rule_description"
          rules={[requiredFormValidation("Description required")]}
        >
          <Input.TextArea
            placeholder="Description"
            rows={3}
            className="EditQcRuleModal__TextArea"
          />
        </Form.Item>

        <Form.List
          name="rule_details"
        >
          {(fields, { add, remove }) => (
            <div className="EditQcRuleModal__ListInputContainer">
              <Flex justify="space-between" align="center" className="EditQcRuleModal__ListInputHeader">
                <label>Details</label>
                <Button type="primary" size="middle" onClick={add}>
                  <PlusOutlined />
                  <span>Add Detail</span>
                </Button>
              </Flex>
              {fields.map(({ key, name, ...restField }) => (
                <Flex align="flex-start" key={key} gap={8}>
                  <Form.Item
                    {...restField} // eslint-disable-line
                    name={[name, "condition"]}
                    rules={[requiredFormValidation("Rule required")]}
                    className="EditQcRuleModal__ListInput"
                  >
                    <Input placeholder="Rule" />
                  </Form.Item>
                  {fields.length > 1 && (
                    <Button
                      type="text"
                      className="EditQcRuleModal__ListInputRemove"
                      onClick={() => remove(name)}
                    >
                      <MinusCircleOutlined />
                    </Button>
                  )}
                </Flex>
              ))}
            </div>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
}
